import { DOCUMENT } from '@angular/common'
import { Injectable, InjectionToken, inject, REQUEST_CONTEXT, RESPONSE_INIT } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'

/**
 * Canonical SEO origin. This MUST match the domain used in the dynamic
 * `sitemap.xml` generated by the Spring Boot backend (and the `Sitemap:` entry
 * in `public/robots.txt`). It is intentionally hardcoded rather than derived
 * from `environment.siteUrl`, which points at the staging/SPA origin and would
 * produce canonical URLs that disagree with the sitemap.
 */
const SEO_BASE_URL = 'https://beautyboss.ir'

/** Default OpenGraph image used when a page has no specific image. */
const DEFAULT_OG_IMAGE = `${SEO_BASE_URL}/assets/images/default-banner.png`

/** Site name appended to every page title and used for `og:site_name`. */
const SITE_NAME = 'بیوتی‌باس'

/** HTML id used to track the canonical link element across route changes. */
const CANONICAL_LINK_ID = 'seo-canonical'

/** Prefix for every injected JSON-LD `<script>` id. */
const JSONLD_ID_PREFIX = 'seo-jsonld-'

/**
 * Supported Schema.org `@type` values for structured-data injection.
 * `RealEstateListing` is the closest fit for space-rental ads (physical spaces
 * for lease); `Product` covers equipment listings. `Course` is the academy
 * course page, `Organization`/`Person` the instructor profile. `WebSite` is
 * used for the homepage and `BreadcrumbList` for navigation context.
 */
export type JsonLdType =
	| 'Product'
	| 'RealEstateListing'
	| 'WebSite'
	| 'BreadcrumbList'
	| 'Course'
	| 'Organization'
	| 'Person'

/**
 * Input contract for {@link SeoService.setSeo}. All fields optional so callers
 * only specify what they have; the service fills in sensible defaults.
 */
export interface SeoConfig {
	/** Page title (without the site-name suffix, which is added automatically). */
	title: string
	/** Short page description for `description` / `og:description` / `twitter:description`. */
	description?: string
	/** Comma-separated keywords for the legacy `keywords` meta tag. */
	keywords?: string
	/**
	 * Path segment of the canonical URL (must start with `/`), e.g.
	 * `/ads/space-rental/abc-123`. Combined with {@link SEO_BASE_URL} to form
	 * the absolute `canonical` and `og:url`.
	 */
	canonicalPath: string
	/** Absolute image URL for `og:image` / `twitter:image`. Falls back to a default. */
	image?: string
	/** OpenGraph object type. Defaults to `website`. */
	type?: 'website' | 'article' | 'product'
	/** `robots` directive. Defaults to `index, follow`; set to `noindex` to opt out. */
	robots?: string
	/** Optional Schema.org structured-data payload. */
	jsonLd?: { type: JsonLdType; data: Record<string, unknown> }
}

/**
 * Shared per-request state object passed through `AngularNodeAppEngine.handle`.
 * The Express layer in `server.ts` creates one per request and reads the
 * `notFound` flag after rendering to decide the final HTTP status. This is a
 * fallback mechanism; the primary 404 signal is {@link RESPONSE_INIT}.
 */
export interface SeoRequestContext {
	notFound?: boolean
}

/**
 * Injection token for the SEO request context, provided per-request on the
 * server via {@link provideSeoRequestContext}. On the browser it resolves to
 * `null`.
 */
export const SEO_REQUEST_CONTEXT = new InjectionToken<SeoRequestContext | null>('SEO_REQUEST_CONTEXT')

/**
 * Resolve the per-request {@link SeoRequestContext} from Angular's internal
 * `REQUEST_CONTEXT` token (populated by the SSR engine from the second argument
 * of `AngularNodeAppEngine.handle`). Returns `null` on the browser. Kept as a
 * standalone factory so it can be registered once in the server config.
 */
export function provideSeoRequestContext() {
	return { provide: SEO_REQUEST_CONTEXT, useFactory: () => inject(REQUEST_CONTEXT, { optional: true }) }
}

/**
 * SSR-aware SEO & meta tag manager.
 *
 * Wraps Angular's {@link Title} and {@link Meta} services plus direct DOM
 * manipulation via {@link DOCUMENT} to set, update, and clean up:
 * - `<title>`
 * - standard meta tags (`description`, `keywords`, `robots`)
 * - OpenGraph tags (`og:*`)
 * - Twitter Card tags (`twitter:*`)
 * - `<link rel="canonical">`
 * - JSON-LD structured-data `<script type="application/ld+json">`
 *
 * All injected elements are tagged with stable ids and removed before
 * re-injection on the next call, so there are **no leaks across route
 * transitions**. The service is safe to call on both the server (SSR/prerender)
 * and the browser: `Title`/`Meta` are SSR-aware, and DOM writes go through the
 * injected `DOCUMENT`.
 *
 * It also exposes {@link setNotFound} to emit a real HTTP 404 during SSR by
 * mutating the `RESPONSE_INIT` object (the same reference the Angular SSR
 * engine uses to construct the final `Response`), with a secondary flag for the
 * Express fallback path.
 */
@Injectable({
	providedIn: 'root',
})
export class SeoService {
	private readonly title = inject(Title)
	private readonly meta = inject(Meta)
	private readonly document = inject(DOCUMENT)
	private readonly responseInit = inject(RESPONSE_INIT, { optional: true })
	private readonly requestContext = inject(SEO_REQUEST_CONTEXT, { optional: true })

	/**
	 * Apply a full set of SEO metadata for the current page. Replaces any
	 * previously injected canonical link and JSON-LD script.
	 */
	setSeo(config: SeoConfig): void {
		const {
			title,
			description,
			keywords,
			canonicalPath,
			image,
			type = 'website',
			robots = 'index, follow',
			jsonLd,
		} = config

		const canonicalUrl = this.buildAbsoluteUrl(canonicalPath)
		const ogImage = image?.trim() ? image : DEFAULT_OG_IMAGE
		const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`

		// --- <title> ---
		this.title.setTitle(fullTitle)

		// --- Standard meta ---
		this.upsertMeta('description', description)
		this.upsertMeta('keywords', keywords)
		this.upsertMeta('robots', robots)

		// --- OpenGraph ---
		this.upsertMetaProperty('og:title', fullTitle)
		this.upsertMetaProperty('og:description', description)
		this.upsertMetaProperty('og:image', ogImage)
		this.upsertMetaProperty('og:url', canonicalUrl)
		this.upsertMetaProperty('og:type', type)
		this.upsertMetaProperty('og:site_name', SITE_NAME)

		// --- Twitter Card ---
		this.upsertMetaName('twitter:card', 'summary_large_image')
		this.upsertMetaName('twitter:title', fullTitle)
		this.upsertMetaName('twitter:description', description)
		this.upsertMetaName('twitter:image', ogImage)

		// --- Canonical link ---
		this.upsertCanonical(canonicalUrl)

		// --- Structured data (JSON-LD) ---
		this.upsertJsonLd(jsonLd, canonicalUrl)
	}

	/**
	 * Convenience setter for generic pages (homepage, listing pages) that only
	 * need a title + canonical URL and an optional structured-data block.
	 */
	setDefaults(title: string, canonicalPath: string, jsonLd?: SeoConfig['jsonLd']): void {
		this.setSeo({
			title,
			canonicalPath,
			type: 'website',
			jsonLd,
		})
	}

	/**
	 * Flag the current SSR response as a 404. Mutates the `RESPONSE_INIT`
	 * object (same reference consumed by the Angular SSR engine when building
	 * the final `Response`) so the emitted HTTP status is 404. Also sets the
	 * secondary `notFound` flag on the SEO request context for the Express
	 * fallback in `server.ts`.
	 *
	 * Has no effect on the browser (where there is no response object); callers
	 * should additionally render a user-facing "not found" UI there.
	 */
	setNotFound(): void {
		if (this.responseInit) {
			this.responseInit.status = 404
		}
		if (this.requestContext) {
			this.requestContext.notFound = true
		}
		// Defense-in-depth: never index a not-found page even if the status
		// code does not propagate for some reason.
		this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' })
	}

	/** Build an absolute canonical URL from a path starting with `/`. */
	private buildAbsoluteUrl(path: string): string {
		const normalized = path.startsWith('/') ? path : `/${path}`
		return `${SEO_BASE_URL}${normalized}`
	}

	/** Insert or update a standard `<meta name="...">` tag. Skips when value is empty. */
	private upsertMeta(name: 'description' | 'keywords' | 'robots', value?: string): void {
		if (!value) return
		this.meta.updateTag({ name, content: value })
	}

	/** Insert or update a `<meta property="og:...">` tag. */
	private upsertMetaProperty(property: string, content?: string): void {
		if (content === undefined) return
		this.meta.updateTag({ property, content })
	}

	/** Insert or update a `<meta name="twitter:...">` tag. */
	private upsertMetaName(name: string, content?: string): void {
		if (content === undefined) return
		this.meta.updateTag({ name, content })
	}

	/**
	 * Replace the canonical `<link rel="canonical">`. Removes any previous
	 * instance (by id) before inserting a fresh one to avoid duplicates.
	 */
	private upsertCanonical(href: string): void {
		const doc = this.document
		doc.getElementById(CANONICAL_LINK_ID)?.remove()
		const link = doc.createElement('link')
		link.id = CANONICAL_LINK_ID
		link.rel = 'canonical'
		link.href = href
		doc.head.appendChild(link)
	}

	/**
	 * Inject a JSON-LD structured-data script. Any previous script for the same
	 * Schema type is removed first to avoid stacking across navigations.
	 */
	private upsertJsonLd(jsonLd: SeoConfig['jsonLd'], canonicalUrl: string): void {
		if (!jsonLd) {
			return
		}
		const id = JSONLD_ID_PREFIX + jsonLd.type.toLowerCase()
		const doc = this.document
		doc.getElementById(id)?.remove()

		const script = doc.createElement('script')
		script.id = id
		script.type = 'application/ld+json'
		// Merge `@context`/`@type`/`url` defaults with caller-supplied data.
		script.textContent = JSON.stringify({
			'@context': 'https://schema.org',
			'@type': jsonLd.type,
			url: canonicalUrl,
			...jsonLd.data,
		})
		doc.head.appendChild(script)
	}
}
